import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Map, FileDown, Leaf, Euro, CalendarSync, RefreshCw, Loader2, ShieldAlert, Mail, Download } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/api";
import { FunnelPanel } from "@/components/admin/FunnelPanel";
import { AffiliatesPanel } from "@/components/admin/AffiliatesPanel";
import { EventsPanel } from "@/components/admin/EventsPanel";

const REFRESH_MS = 15000;
const dt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
const eur = (n) => `${(n || 0).toFixed(2).replace(".", ",")} €`;
const STATUS_TONE = { paid: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800", ready: "bg-emerald-100 text-emerald-800", generating: "bg-sky-100 text-sky-800", active: "bg-emerald-100 text-emerald-800", error: "bg-red-100 text-red-700", failed: "bg-red-100 text-red-700", expired: "bg-gray-100 text-gray-500", canceled: "bg-gray-100 text-gray-500", past_due: "bg-red-100 text-red-700" };
const STATUS_IT = { paid: "pagato", pending: "in attesa", failed: "fallito", expired: "scaduto", refunded: "rimborsato", ready: "pronto", generating: "in corso", error: "errore", active: "attivo", canceled: "disdetto", trialing: "prova", past_due: "insoluto" };
const Pill = ({ v }) => <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_TONE[v] || "bg-gray-100 text-gray-600"}`}>{STATUS_IT[v] || v || "—"}</span>;

const Kpi = ({ icon: Icon, label, value, sub, id }) => (
  <div data-testid={id} className="rounded-3xl border border-emerald-100 bg-white p-6">
    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500"><Icon className="h-4 w-4 text-emerald-600" />{label}</p>
    <p className="mt-3 font-display text-3xl font-extrabold text-gray-900">{value}</p>
    {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
  </div>
);

const Table = ({ id, cols, rows, empty }) => (
  <div data-testid={id} className="overflow-x-auto rounded-3xl border border-emerald-100 bg-white">
    <table className="w-full text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-widest text-gray-500">
        <tr>{cols.map((c) => <th key={c.k} className="px-5 py-3 font-semibold">{c.l}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-5 py-6 text-gray-500">{empty}</td></tr>}
        {rows.map((r, i) => <tr key={i} className="hover:bg-emerald-50/40">{cols.map((c) => <td key={c.k} className="px-5 py-3 text-gray-700">{c.r ? c.r(r) : r[c.k] ?? "—"}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);

const TRIP_COLS = [
  { k: "created_at", l: "Data", r: (t) => dt(t.created_at) },
  { k: "destination", l: "Destinazione", r: (t) => <Link to={`/viaggio/${t.id}`} className="font-semibold text-emerald-700 hover:underline">{t.destination || t.prompt.slice(0, 40)}</Link> },
  { k: "days", l: "Giorni" },
  { k: "tis", l: "TIS", r: (t) => (t.tis != null ? <strong className="font-display text-gray-900">{t.tis}</strong> : "—") },
  { k: "owner", l: "Utente", r: (t) => t.owner || <span className="text-gray-400">ospite</span> },
  { k: "status", l: "Stato", r: (t) => <Pill v={t.status} /> },
];
const PAY_COLS = [
  { k: "created_at", l: "Data", r: (p) => dt(p.created_at) },
  { k: "plan", l: "Piano", r: (p) => (p.plan === "monthly" ? "Abbonamento mensile" : "PDF singolo") },
  { k: "amount", l: "Importo", r: (p) => (p.currency === "usd" ? `$${(p.amount || 0).toFixed(2)}` : eur(p.amount)) },
  { k: "user", l: "Utente" },
  { k: "payment_status", l: "Pagamento", r: (p) => <Pill v={p.payment_status} /> },
  { k: "session_id", l: "Sessione", r: (p) => <code className="text-xs text-gray-400">…{p.session_id}</code> },
];
const USER_COLS = [
  { k: "created_at", l: "Registrato", r: (u) => dt(u.created_at) },
  { k: "email", l: "Email" },
  { k: "name", l: "Nome" },
  { k: "trips", l: "Viaggi" },
  { k: "subscription", l: "Abbonamento", r: (u) => <Pill v={u.subscription} /> },
];

const SUB_COLS = [
  { k: "consent_at", l: "Consenso", r: (s) => (s.consent_at ? dt(s.consent_at) : "—") },
  { k: "email", l: "Email" },
  { k: "lang", l: "Lingua", r: (s) => (s.lang || "—").toUpperCase() },
  { k: "marketing", l: "Marketing", r: (s) => (s.marketing ? "Sì" : "No") },
  { k: "last_destination", l: "Ultimo viaggio", r: (s) => (s.last_trip_id ? <Link to={`/viaggio/${s.last_trip_id}`} className="font-semibold text-emerald-700 hover:underline">{s.last_destination || "apri"}</Link> : "—") },
  { k: "trips", l: "Itinerari distinti" },
];
const csvCell = (v) => `"${String(v ?? "").replace(/^([=+\-@])/, "'$1").replace(/"/g, '""')}"`;
const exportCsv = (rows) => {
  const head = ["email", "lingua", "marketing", "data_consenso", "primo_invio", "ultima_destinazione", "link_ultimo_viaggio", "itinerari_distinti"];
  const lines = rows.map((s) => [s.email, s.lang, s.marketing ? "si" : "no", s.consent_at, s.created_at, s.last_destination,
    s.last_trip_id ? `${window.location.origin}/viaggio/${s.last_trip_id}` : "", s.trips].map(csvCell).join(","));
  const blob = new Blob(["\uFEFF" + [head.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `tripsaver-iscritti-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const TABS = [["trips", "Itinerari"], ["subscribers", "Iscritti email"], ["affiliates", "Gestione Affiliazioni"], ["payments", "Pagamenti Stripe"], ["users", "Utenti"]];

const Dashboard = () => {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("trips");
  const [updated, setUpdated] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    try {
      const [stats, trips, payments, users, subscribers] = await Promise.all([adminApi.stats(), adminApi.trips(), adminApi.payments(), adminApi.users(), adminApi.subscribers()]);
      setD({ stats, trips, payments, users, subscribers }); setUpdated(new Date()); setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, REFRESH_MS); return () => clearInterval(t); }, [load]);
  if (!d && failed) return <p data-testid="admin-load-error" className="rounded-3xl bg-red-50 p-6 text-red-700">Impossibile caricare i dati. Riprovo automaticamente tra 15 secondi.</p>;
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />;
  const s = d.stats, paid = s.payments.paid || [];
  const byCur = paid.reduce((acc, r) => ({ ...acc, [r.currency]: (acc[r.currency] || 0) + r.total }), {});
  const revenueText = Object.entries(byCur).map(([c, v]) => (c === "usd" ? `$${v.toFixed(2)}` : eur(v))).join(" + ") || eur(0);
  const countOf = (plan) => paid.filter((r) => r.plan === plan).reduce((n, r) => n + r.count, 0);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6 flex items-center justify-between text-sm text-gray-500">
        <p data-testid="admin-last-updated" className="flex items-center gap-2"><span className={`h-2 w-2 animate-pulse rounded-full ${failed ? "bg-red-500" : "bg-emerald-500"}`} />{failed ? "Ultimo aggiornamento non riuscito" : "Aggiornamento automatico ogni 15 s"} · {updated?.toLocaleTimeString("it-IT")} · ultimi 100 record per tabella</p>
        <button data-testid="admin-refresh-button" onClick={() => load()} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-semibold text-gray-700 ring-1 ring-emerald-100 hover:bg-emerald-50"><RefreshCw className="h-4 w-4" />Aggiorna</button>
      </div>
      <FunnelPanel tick={updated} />
      <EventsPanel />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Kpi id="kpi-users" icon={Users} label="Utenti registrati" value={s.users} />
        <Kpi id="kpi-trips" icon={Map} label="Preventivi generati" value={s.trips.total} sub={`${s.trips.by_status.ready || 0} pronti · ${s.trips.by_status.error || 0} errori · ${s.trips.saved} salvati da utenti`} />
        <Kpi id="kpi-pdfs" icon={FileDown} label="PDF sbloccati" value={s.pdfs.total} sub={`${s.pdfs.by_source.single || 0} singoli · ${s.pdfs.by_source.subscription || 0} da abbonamento`} />
        <Kpi id="kpi-tis" icon={Leaf} label="TIS medio" value={s.tis ? `${String(s.tis.avg).replace(".", ",")}/100` : "—"} sub={s.tis ? `min ${s.tis.min} · max ${s.tis.max}` : ""} />
        <Kpi id="kpi-revenue" icon={Euro} label="Incassi (pagati)" value={revenueText} sub={`${countOf("single")} × PDF singolo · ${countOf("monthly")} × abbonamento`} />
        <Kpi id="kpi-subscribers" icon={Mail} label="Iscritti email" value={s.subscribers ?? 0} sub="con consenso privacy per consigli di viaggio" />
        <Kpi id="kpi-subscriptions" icon={CalendarSync} label="Abbonamenti attivi" value={s.subscriptions.active} sub={`${s.subscriptions.canceling} in disdetta · ${s.payments.by_status.paid || 0} pagamenti riusciti, ${s.payments.by_status.pending || 0} in attesa`} />
      </div>
      <div className="mt-10 flex gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === k ? "bg-gray-900 text-white" : "bg-white text-gray-600 ring-1 ring-emerald-100 hover:bg-emerald-50"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-5">
        {tab === "trips" && <Table id="admin-trips-table" cols={TRIP_COLS} rows={d.trips} empty="Nessun itinerario" />}
        {tab === "subscribers" && (
          <div className="space-y-4">
            <button data-testid="admin-export-csv-button" onClick={() => exportCsv(d.subscribers)} disabled={!d.subscribers.length}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-500 disabled:opacity-50"><Download className="h-4 w-4" />Esporta CSV ({d.subscribers.length})</button>
            <Table id="admin-subscribers-table" cols={SUB_COLS} rows={d.subscribers} empty="Nessun iscritto" />
          </div>
        )}
        {tab === "affiliates" && <AffiliatesPanel />}
        {tab === "payments" && <Table id="admin-payments-table" cols={PAY_COLS} rows={d.payments} empty="Nessun pagamento" />}
        {tab === "users" && <Table id="admin-users-table" cols={USER_COLS} rows={d.users} empty="Nessun utente" />}
      </div>
    </div>
  );
};

export default function Admin() {
  const { user, openAuth } = useAuth();
  return (
    <PageShell testId="admin-page" eyebrow="Area riservata" title="Dashboard Admin">
      {user === null && <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />}
      {user === false && (
        <button data-testid="admin-login-button" onClick={() => openAuth("Accedi con l'account amministratore.")} className="rounded-full bg-emerald-600 px-6 py-3 font-semibold text-white">Accedi</button>
      )}
      {user && user.role !== "admin" && (
        <p data-testid="admin-forbidden" className="flex items-center gap-2 rounded-3xl bg-white p-8 text-gray-700 ring-1 ring-emerald-100"><ShieldAlert className="h-5 w-5 text-red-500" />Accesso riservato all'amministratore.</p>
      )}
      {user?.role === "admin" && <Dashboard />}
    </PageShell>
  );
}
